// @oasis-permission-inherits: /capcode (capcode:view)
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

import { InfiniteQueryPagination } from "~/components/infinite-query-pagination";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { fetchCapCodeReviewFeedbackQuery } from "~/modules/capcode-review/feedback";
import { formatRelativeTime } from "~/modules/capcode-review/format";
import { shouldShowReviewFeedbackEmptyState } from "~/modules/capcode-review/review-feedback-empty-state";
import { shouldFetchReviewFeedbackNextPage } from "~/modules/capcode-review/review-feedback-pagination";

const searchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_dash/capcode/review-feedback")({
  validateSearch: searchSchema,
  component: ReviewFeedbackPage,
});

function ReviewFeedbackPage() {
  const { page } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isFetchNextPageError,
    error,
  } = fetchCapCodeReviewFeedbackQuery.useInfiniteQuery({ params: {} });

  const totalPages = data?.pages.length ?? 0;
  const targetPageIndex = page ? page - 1 : 0;
  const [currentPageIndex, setCurrentPageIndex] = useState(targetPageIndex);

  useEffect(() => {
    if (
      shouldFetchReviewFeedbackNextPage({
        targetPageIndex,
        totalPages,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
      })
    ) {
      fetchNextPage();
    }
  }, [
    targetPageIndex,
    totalPages,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  ]);

  useEffect(() => {
    if (targetPageIndex < totalPages) {
      setCurrentPageIndex(targetPageIndex);
    } else if (!hasNextPage && totalPages > 0) {
      setCurrentPageIndex(totalPages - 1);
    }
  }, [targetPageIndex, totalPages, hasNextPage]);

  const currentPage = data?.pages[currentPageIndex];
  const feedbackItems = currentPage?.items ?? [];

  const onPreviousPage = () => {
    const previousPageIndex = Math.max(0, currentPageIndex - 1);
    setCurrentPageIndex(previousPageIndex);
    navigate({ search: { page: previousPageIndex + 1 } });
  };

  const onNextPage = async () => {
    const nextPageIndex = currentPageIndex + 1;
    setCurrentPageIndex(nextPageIndex);
    navigate({ search: { page: nextPageIndex + 1 } });
    if (nextPageIndex === totalPages) {
      await fetchNextPage();
    }
  };

  const onPageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
    navigate({ search: { page: pageIndex + 1 } });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">CapCode Finding Feedback</h1>
        <p className="text-muted-foreground text-sm">
          Feedback submitted by reviewers for CapCode findings, newest first.
        </p>
      </div>

      {error && !isFetchNextPageError ? (
        <div className="text-red-600 text-sm">Failed to load feedback: {String(error)}</div>
      ) : null}

      {isFetchNextPageError ? (
        <div className="flex items-center gap-3 text-sm text-red-600">
          <span>Failed to load the next page: {String(error)}</span>
          <Button variant="outline" size="sm" onClick={() => void fetchNextPage()}>
            Retry
          </Button>
        </div>
      ) : null}

      {totalPages > 0 && !isLoading ? (
        <div className="flex justify-end">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage && !isFetchNextPageError}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>PR</TableHead>
              <TableHead>Finding</TableHead>
              <TableHead>Feedback</TableHead>
              <TableHead>Reviewer</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-12 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            {shouldShowReviewFeedbackEmptyState({
              isLoading,
              error,
              currentPageItemCount: currentPage?.items.length,
            }) ? (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground text-center">
                  No finding feedback submitted yet.
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading &&
              feedbackItems.map((item) => (
                <TableRow key={`${item.feedbackId}:${item.authorUserId}`}>
                  <TableCell className="whitespace-nowrap align-top">
                    <a
                      href={`https://github.com/${item.repo}/pull/${item.pr}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium hover:underline"
                    >
                      {item.repo}#{item.pr} ↗
                    </a>
                    <a
                      href={item.githubCommentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-muted-foreground mt-1 block max-w-56 truncate font-mono text-xs underline-offset-4 hover:underline"
                    >
                      {item.path}:{item.line} ↗
                    </a>
                  </TableCell>
                  <TableCell className="max-w-80 align-top">
                    <div className="flex items-center gap-2">
                      <Link
                        to="/review-feedback/$feedbackId"
                        params={{ feedbackId: item.feedbackId }}
                        className="font-medium underline-offset-4 hover:underline"
                      >
                        {item.findingTitle || "Untitled finding"}
                      </Link>
                      <Badge variant="outline">{item.severity}</Badge>
                    </div>
                    <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                      {item.findingBody}
                    </p>
                  </TableCell>
                  <TableCell className="max-w-96 align-top">
                    <p className="line-clamp-3 whitespace-pre-wrap text-sm">{item.feedback}</p>
                  </TableCell>
                  <TableCell className="max-w-48 truncate align-top font-mono text-xs">
                    {item.authorUserId}
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap align-top text-xs">
                    {formatRelativeTime(item.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
